import { usePopup } from '../context/PopupContext';
import { Shield, Check, Users, Award, Clock, CheckCircle, FileText, ShieldCheck, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import LeadForm from '../components/LeadForm';
import RevisedPricingSection from '../components/RevisedPricingSection';
import TestimonialsSlider from '../components/TestimonialsSlider';
import LPCTABanner from '../components/LPCTABanner';
import LPFAQSection from '../components/LPFAQSection';
import { useState, useCallback } from 'react';

const benefits = [
  { icon: Shield, title: "Nationwide Protection", desc: "Secure exclusive rights to your brand across all 50 states with federal trademark registration." },
  { icon: FileText, title: "Complete Documentation", desc: "Every document meticulously prepared and filed with the USPTO on your behalf." },
  { icon: Users, title: "Expert Guidance", desc: "Our IP professionals guide you through every step of the registration process." },
  { icon: Award, title: "High Approval Rate", desc: "Our thorough preparation leads to one of the highest approval rates in the industry." }
];

const whyUs = [
  "Over 15,000 trademarks successfully filed",
  "Dedicated case managers for every client",
  "Comprehensive clearance search included",
  "Full office action response support",
  "Continuous trademark monitoring available",
  "98% client satisfaction rate"
];

export default function LandingPage() {
  const { openPopup } = usePopup();
  const [benefitsIndex, setBenefitsIndex] = useState(0);
  const [benefitsDrag, setBenefitsDrag] = useState({ isDragging: false, startX: 0, currentX: 0 });

  const totalBenefits = benefits.length;
  const maxBenefitsIndex = totalBenefits - 1;

  const benefitsNext = useCallback(() => {
    setBenefitsIndex(prev => (prev >= maxBenefitsIndex ? 0 : prev + 1));
  }, [maxBenefitsIndex]);

  const benefitsPrev = useCallback(() => {
    setBenefitsIndex(prev => (prev <= 0 ? maxBenefitsIndex : prev - 1));
  }, [maxBenefitsIndex]);

  const handleBenefitsMouseDown = useCallback((e) => {
    setBenefitsDrag({ isDragging: true, startX: e.clientX, currentX: 0 });
  }, []);

  const handleBenefitsMouseMove = useCallback((e) => {
    if (!benefitsDrag.isDragging) return;
    const diff = e.clientX - benefitsDrag.startX;
    setBenefitsDrag(prev => ({ ...prev, currentX: diff }));
  }, [benefitsDrag.isDragging, benefitsDrag.startX]);

  const handleBenefitsMouseUp = useCallback(() => {
    if (!benefitsDrag.isDragging) return;
    const threshold = 50;
    if (benefitsDrag.currentX > threshold) {
      benefitsPrev();
    } else if (benefitsDrag.currentX < -threshold) {
      benefitsNext();
    }
    setBenefitsDrag({ isDragging: false, startX: 0, currentX: 0 });
  }, [benefitsDrag.isDragging, benefitsDrag.currentX, benefitsPrev, benefitsNext]);

  const handleBenefitsMouseLeave = useCallback(() => {
    if (benefitsDrag.isDragging) {
      setBenefitsDrag({ isDragging: false, startX: 0, currentX: 0 });
    }
  }, [benefitsDrag.isDragging]);

  return (
    <main data-testid="landing-page">
      <SEOHead
        title="Register Your Trademark | Starting at $45 | Trademark Legal Forum"
        description="Register your trademark starting at $45 + federal filing fee. Expert USPTO filing, comprehensive clearance search, and dedicated case managers. Protect your brand today."
      />
      {/* 1. Hero with Form */}
      <section data-testid="lp-hero" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/90678b74-8893-4692-bfc7-d7847fcc33f8/images/c927193ed8f5a9f15489e086f7bfe3ce2b3009562c75ee2a007e373fdb65b63a.png"
            alt="Hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0A1128]/80" />
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <p className="uppercase text-sm tracking-[0.2em] font-bold text-[#D4AF37] mb-6 font-['Outfit'] animate-fade-up">
                Trademark Registration Service
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Outfit'] tracking-tight leading-none mb-6 animate-fade-up stagger-1">
                Trademark Your  <span className="gold-gradient-text">Business Name From $45</span>
              </h1>
              <p className="text-base md:text-lg text-[#A0AABF] leading-relaxed mb-8 animate-fade-up stagger-2">
                Protect your brand name, logo, or slogan with federal trademark registration. Our expert team handles everything from comprehensive searches to USPTO filing.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-up stagger-3">
                <button
                  data-testid="lp-hero-register-btn"
                  onClick={() => openPopup()}
                  className="bg-[#D4AF37] text-[#0A1128] font-bold py-4 px-10 rounded-sm text-sm transition-all duration-300 hover:bg-[#F3E5AB] hover:-translate-y-1 shadow-[0_4px_14px_0_rgba(212,175,55,0.39)] font-['Outfit'] tracking-wider uppercase"
                >
                  Register My Trademark
                </button>
                <button
                  data-testid="lp-hero-chat-btn"
                  className="js-chat border border-[#D4AF37]/50 text-[#D4AF37] font-bold py-4 px-10 rounded-sm text-sm transition-all duration-300 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] hover:-translate-y-1 font-['Outfit'] tracking-wider uppercase"
                >
                  Chat Now
                </button>
              </div>
            </div>

            {/* Banner Form */}
            <div data-testid="lp-banner-form" className="order-1 lg:order-2 bg-[#121E3D]/90 backdrop-blur-xl border border-[#1D2D50] rounded-sm p-6 md:p-8 shadow-2xl animate-fade-up stagger-2">
              <h3 className="text-xl font-bold text-white font-['Outfit'] mb-2">Get Trademark Protection for <span class="text-[#D4AF37]">$45</span></h3>
              <p className="text-[#A0AABF] text-sm mb-6">Share your details and our team will contact you.</p>
              <LeadForm isInline />
            </div>
          </div>
        </div>
      </section>

      {/* 2. Info Section */}
      <section data-testid="lp-info" className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#D4AF37]/5 rounded-sm" />
              <img
                src="/assets/img/lp-about-sec.webp"
                alt="Trademark expertise"
                className="relative rounded-sm w-full h-auto object-cover shadow-2xl"
              />
            </div>
            <div>
              <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#D4AF37] mb-4 font-['Outfit']">Why Register?</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 font-['Outfit'] tracking-tight mb-6">
                Your Brand Is Your Most Valuable Asset
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Without federal trademark protection, your brand remains vulnerable to copycats and infringement. A registered trademark gives you the exclusive legal right to use your brand name, logo, or slogan across the entire United States.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Our trademark registration service provides end-to-end support, from initial clearance search through USPTO filing and beyond. We make brand protection simple, affordable, and effective.
              </p>
              <ul className="space-y-2">
                {whyUs.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm">
                    <Check size={16} className="text-[#D4AF37] flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid="lp-info-register-btn"
                onClick={() => openPopup()}
                className="bg-[#D4AF37] text-[#0A1128] font-bold py-3 px-8 rounded-sm text-sm transition-all duration-300 hover:bg-[#F3E5AB] hover:-translate-y-0.5 shadow-[0_4px_14px_0_rgba(212,175,55,0.39)] font-['Outfit'] tracking-wide mt-6"
              >
                Register My Trademark
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Benefits */}
      <section data-testid="lp-benefits" className="py-20 md:py-32 bg-[#0A1128]">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#D4AF37] mb-4 font-['Outfit']">Benefits</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Outfit'] tracking-tight">
              Why Choose Trademark Legal Forum
            </h2>
          </div>
          <div className="relative">
            {/* Mobile slider */}
            <div className="overflow-hidden sm:hidden">
              <div 
                className="flex transition-transform duration-300 ease-in-out gap-4"
                style={{ transform: `translateX(-${benefitsIndex * 100}%)` }}
                onMouseDown={handleBenefitsMouseDown}
                onMouseMove={handleBenefitsMouseMove}
                onMouseUp={handleBenefitsMouseUp}
                onMouseLeave={handleBenefitsMouseLeave}
              >
                {benefits.map((b, i) => {
                  const Icon = b.icon;
                  return (
                    <div key={i} data-testid={`lp-benefit-${i}`} className="min-w-full bg-[#000000] border border-[#1D2D50] rounded-sm p-8 text-center">
                      <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-5">
                        <Icon size={24} className="text-[#D4AF37]" />
                      </div>
                      <h3 className="text-lg font-bold text-white font-['Outfit'] mb-3">{b.title}</h3>
                      <p className="text-[#A0AABF] text-sm leading-relaxed">{b.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Desktop grid */}
            <div className="hidden sm:grid grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((b, i) => {
                const Icon = b.icon;
                return (
                  <div key={i} data-testid={`lp-benefit-${i}`} className="bg-[#000000] border border-[#1D2D50] rounded-sm p-8 text-center hover:border-[#D4AF37]/30 hover:-translate-y-1 transition-all duration-300">
                    <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-5">
                      <Icon size={24} className="text-[#D4AF37]" />
                    </div>
                    <h3 className="text-lg font-bold text-white font-['Outfit'] mb-3">{b.title}</h3>
                    <p className="text-[#A0AABF] text-sm leading-relaxed">{b.desc}</p>
                  </div>
                );
              })}
            </div>

            {/* Mobile navigation arrows */}
            <div className="flex justify-between items-center mt-6 sm:hidden">
              <button
                onClick={benefitsPrev}
                className="w-10 h-10 bg-[#0A1128] border border-[#1D2D50] rounded-full flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              <div className="flex gap-2">
                {Array.from({ length: totalBenefits }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setBenefitsIndex(i)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      benefitsIndex === i ? 'bg-[#D4AF37] w-6' : 'bg-[#1D2D50]'
                    }`}
                  />
                ))}
              </div>
              <button
                onClick={benefitsNext}
                className="w-10 h-10 bg-[#0A1128] border border-[#1D2D50] rounded-full flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Pricing */}
      <RevisedPricingSection />

      {/* 5. Why Choose Us Details */}
      <section data-testid="lp-why-us" className="py-20 md:py-32 bg-[#0D1729]">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="uppercase text-sm tracking-[0.2em] font-bold text-[#D4AF37] mb-4 font-['Outfit']">Trusted Service</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Outfit'] tracking-tight mb-6">
                Thousands of Businesses Trust Us
              </h2>
              <p className="text-[#A0AABF] leading-relaxed mb-6">
                Since our inception, we have helped thousands of businesses protect their brands through federal trademark registration. Our team of experienced IP professionals ensures that every application is meticulously prepared and strategically positioned for approval.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#000000] border border-[#1D2D50] rounded-sm p-4 text-center">
                  <p className="text-2xl font-bold gold-gradient-text font-['Outfit']">15K+</p>
                  <p className="text-[#A0AABF] text-xs mt-1">Trademarks Filed</p>
                </div>
                <div className="bg-[#000000] border border-[#1D2D50] rounded-sm p-4 text-center">
                  <p className="text-2xl font-bold gold-gradient-text font-['Outfit']">98%</p>
                  <p className="text-[#A0AABF] text-xs mt-1">Satisfaction Rate</p>
                </div>
                <div className="bg-[#000000] border border-[#1D2D50] rounded-sm p-4 text-center">
                  <p className="text-2xl font-bold gold-gradient-text font-['Outfit']">50</p>
                  <p className="text-[#A0AABF] text-xs mt-1">States Covered</p>
                </div>
                <div className="bg-[#000000] border border-[#1D2D50] rounded-sm p-4 text-center">
                  <p className="text-2xl font-bold gold-gradient-text font-['Outfit']">24/7</p>
                  <p className="text-[#A0AABF] text-xs mt-1">Expert Support</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/90678b74-8893-4692-bfc7-d7847fcc33f8/images/75874694900d1a5f30eb84fdc682a7cc3e0e18642e96009ba65ddd97cb4bfe0d.png"
                alt="Trust"
                className="rounded-sm w-full h-auto object-cover shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 7. Testimonials */}
      <TestimonialsSlider />

      {/* 8. What You Get */}
      <section data-testid="lp-what-you-get" className="py-20 md:py-32 bg-[#0A1128]">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#D4AF37] mb-4 font-['Outfit']">What You Get</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Outfit'] tracking-tight mb-6">
                Complete Trademark Protection From Start to Finish
              </h2>
              <p className="text-[#A0AABF] leading-relaxed mb-8">
                When you choose Trademark Legal Forum, you are not just filing paperwork. You are gaining a dedicated team of intellectual property professionals who will protect your brand at every stage, from the initial clearance search all the way through registration and beyond.
              </p>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Search size={18} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold font-['Outfit'] mb-1">Comprehensive Clearance Search</h4>
                    <p className="text-[#A0AABF] text-sm leading-relaxed">We analyze federal and state databases, common law sources, and domain registrations to identify any potential conflicts before you file.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <FileText size={18} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold font-['Outfit'] mb-1">Expert Application Preparation</h4>
                    <p className="text-[#A0AABF] text-sm leading-relaxed">Our specialists prepare every detail of your USPTO application, selecting the right classes, goods, and services to maximize your protection.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <ShieldCheck size={18} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold font-['Outfit'] mb-1">Ongoing Monitoring & Support</h4>
                    <p className="text-[#A0AABF] text-sm leading-relaxed">After registration, we continue to monitor for potential infringements and handle any office actions or challenges that may arise.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-[#121E3D] border border-[#1D2D50] rounded-sm p-8 md:p-10">
                <h3 className="text-xl font-bold text-white font-['Outfit'] mb-6 pb-4 border-b border-[#1D2D50]">Every Plan Includes</h3>
                <ul className="space-y-4">
                  {[
                    "Personalized case review by an IP specialist",
                    "Federal trademark search and analysis",
                    "Complete USPTO application preparation",
                    "Electronic filing with the USPTO",
                    "Status updates throughout the process",
                    "Office action response support",
                    "Digital copies of all filed documents",
                    "Dedicated customer service team"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle size={16} className="text-[#D4AF37] flex-shrink-0" />
                      <span className="text-white/80 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <LPFAQSection />

      {/* 10. CTA Banner */}
      <LPCTABanner />
    </main>
  );
}
